import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useAuth } from './AuthContext';
import xbApi from '../services/xbApi';
import { isPublicPath } from '../utils/publicPaths';

/**
 * The participant's cross-border schemes (scheme profile assignments) and the
 * one its Cross-border pages are showing.
 *
 * `schemes` is null until known, then a list: empty means no Cross-border tab.
 * Assignments are made by an operator and can change while a participant is
 * signed in, so the list is read again whenever the tab comes back, as the
 * corridors are.
 */
const SchemeContext = createContext({
  schemes: null, selected: null, select: () => {}, reload: () => {},
});

const SELECTED_KEY = 'portal.xbScheme';

export function SchemeProvider({ children }) {
  const { user } = useAuth();
  const [schemes, setSchemes] = useState(null);
  const [selectedId, setSelectedId] = useState(() => {
    try { return sessionStorage.getItem(SELECTED_KEY); } catch { return null; }
  });

  const onIso = (user?.corridors || ['FSPIOP', 'ISO20022']).map((c) => String(c).toUpperCase()).includes('ISO20022');

  const reload = useCallback(async () => {
    if (!user || !localStorage.getItem('token') || isPublicPath()) { setSchemes(null); return; }
    if (!onIso) { setSchemes([]); return; }
    try {
      const list = await xbApi.mySchemes();
      setSchemes(Array.isArray(list) ? list : []);
    } catch {
      // Single-tenant mode, or the ISO engine not answering: no Cross-border tab.
      setSchemes([]);
    }
  }, [user, onIso]);

  useEffect(() => {
    reload();
    const onVisible = () => { if (document.visibilityState === 'visible') reload(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [reload]);

  const select = useCallback((id) => {
    setSelectedId(id);
    try { sessionStorage.setItem(SELECTED_KEY, id); } catch { /* storage unavailable */ }
  }, []);

  const selected = useMemo(() => (schemes || []).find((s) => s.profileId === selectedId)
    || (schemes || [])[0] || null, [schemes, selectedId]);

  const value = useMemo(() => ({ schemes, selected, select, reload }), [schemes, selected, select, reload]);
  return <SchemeContext.Provider value={value}>{children}</SchemeContext.Provider>;
}

/** { schemes, selected, select(profileId), reload() } */
export const useSchemes = () => useContext(SchemeContext);
